// Numeri, date e durate nel formato della lingua del sito (e nel fuso orario del visitatore).

import { t } from '../i18n';
import { ALPHA3 } from './paesi-codici';

const cache = new Map<string, Intl.NumberFormat | Intl.RelativeTimeFormat | Intl.DisplayNames>();
const locale = () => t().meta.locale;
function memo<T extends Intl.NumberFormat | Intl.RelativeTimeFormat | Intl.DisplayNames>(chiave: string, crea: () => T): T {
  const k = `${locale()}|${chiave}`;
  let f = cache.get(k) as T | undefined;
  if (!f) { f = crea(); cache.set(k, f); }
  return f;
}

/** `useGrouping: 'always'` (ES2023, tutti i browser attuali): le definizioni di TypeScript in uso non lo conoscono ancora. */
const SEMPRE = 'always' as unknown as boolean;

/** Numero nel formato locale con esattamente `cifre` decimali (default: intero). Separatore delle migliaia sempre,
 *  anche a 4 cifre (in italiano e spagnolo di norma manca: «6659» accanto a «18.591» nella stessa colonna stonava). */
export function n(x: number, cifre = 0): string {
  return memo(`n${cifre}`, () => new Intl.NumberFormat(locale(), { minimumFractionDigits: cifre, maximumFractionDigits: cifre, useGrouping: SEMPRE })).format(x);
}

export const percento = (x: number, cifre = 1) =>
  memo(`p${cifre}`, () => new Intl.NumberFormat(locale(), { style: 'percent', minimumFractionDigits: cifre, maximumFractionDigits: cifre })).format(x);

export function data(iso: string, opzioni: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }): string {
  return new Intl.DateTimeFormat(locale(), opzioni).format(new Date(iso));
}

export function dataOra(iso: string): string {
  return new Intl.DateTimeFormat(locale(), { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
}

/** «tra 3 ore», «2 giorni fa» */
export function tempoRelativo(iso: string, ora = Date.now()): string {
  const s = (new Date(iso).getTime() - ora) / 1000;
  const a = Math.abs(s);
  const relativo = memo('rel', () => new Intl.RelativeTimeFormat(locale(), { numeric: 'auto' }));
  if (a < 60) return relativo.format(Math.round(s), 'second');
  if (a < 3600) return relativo.format(Math.round(s / 60), 'minute');
  if (a < 86400) return relativo.format(Math.round(s / 3600), 'hour');
  if (a < 86400 * 45) return relativo.format(Math.round(s / 86400), 'day');
  return relativo.format(Math.round(s / (86400 * 30)), 'month');
}

/** Conto alla rovescia «T−2g 04:12:09» / «T+00:03:10» */
export function contoAllaRovescia(iso: string, ora = Date.now()): { segno: '−' | '+'; giorni: number; testo: string } {
  let s = Math.round((new Date(iso).getTime() - ora) / 1000);
  const segno = s >= 0 ? '−' : '+';
  s = Math.abs(s);
  const giorni = Math.floor(s / 86400);
  const hh = String(Math.floor((s % 86400) / 3600)).padStart(2, '0');
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return { segno, giorni, testo: `${giorni ? `${giorni}${t().comune.giorniBreve} ` : ''}${hh}:${mm}:${ss}` };
}


export { alpha3DaSatcat } from './paesi-codici';

export function nomePaese(alpha3: string | null): string {
  if (!alpha3) return '';
  const a2 = ALPHA3[alpha3];
  try {
    return a2 ? (memo('paesi', () => new Intl.DisplayNames([locale()], { type: 'region' })).of(a2) ?? alpha3) : alpha3;
  } catch { return alpha3; }
}

/** Bandiera emoji da codice ISO alpha-3 (vuota se sconosciuto). */
export function bandiera(alpha3: string | null): string {
  const a2 = alpha3 ? ALPHA3[alpha3] : undefined;
  if (!a2) return '';
  return String.fromCodePoint(...[...a2].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}
